import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { UserRole } from '@bridgewerk/shared';
import { USER_ROLES } from '@bridgewerk/shared';
import type { Machine } from '@bridgewerk/shared';
import { Button } from '../../../components/ui/Button.js';
import { useInviteUser } from '../hooks/useTeam.js';

const ROLE_LABELS: Record<UserRole, string> = {
  manager:    'Manager — full access, can manage team',
  programmer: 'Programmer — assigned machines, program + operator modes',
  operator:   'Operator — assigned machines, operator mode only',
};

export interface InviteModalProps {
  readonly shopId: string;
  readonly machines: Machine[];
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function InviteModal({ shopId, machines, onClose, onSuccess }: InviteModalProps): JSX.Element {
  const [email, setEmail]       = useState('');
  const [role, setRole]         = useState<UserRole>('operator');
  const [machineIds, setMachineIds] = useState<string[]>([]);
  const [message, setMessage]   = useState('');
  const [error, setError]       = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const invite = useInviteUser(shopId);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const toggleMachine = (id: string) => {
    setMachineIds(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await invite.mutateAsync({
        email: email.trim(),
        role,
        machine_ids: machineIds,
        message: message.trim() || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className="relative w-full max-w-md rounded-xl border border-border overflow-hidden"
        style={{ backgroundColor: 'rgba(11,16,23,0.97)' }}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="font-mono text-sm text-text">Invite team member</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-dim hover:text-text text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={e => void handleSubmit(e)} className="p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted block">
              Email address
            </label>
            <input
              ref={emailRef}
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text placeholder:text-muted focus:border-accent focus:outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted block">
              Role
            </label>
            <div className="space-y-1.5">
              {USER_ROLES.map(r => (
                <label key={r} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="role"
                    value={r}
                    checked={role === r}
                    onChange={() => setRole(r)}
                    className="mt-0.5 accent-accent"
                  />
                  <span className="text-xs font-mono text-text-dim group-hover:text-text">
                    {ROLE_LABELS[r]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {role !== 'manager' && machines.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted block">
                Pre-assign machines <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                {machines.map(m => (
                  <label key={m.machine_id} className="flex items-center gap-2 cursor-pointer group px-2 py-1.5 rounded border border-border hover:border-accent/40">
                    <input
                      type="checkbox"
                      checked={machineIds.includes(m.machine_id)}
                      onChange={() => toggleMachine(m.machine_id)}
                      className="accent-accent"
                    />
                    <span className="text-[11px] font-mono text-text-dim group-hover:text-text truncate">
                      {m.machine_id}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted block">
              Personal note <span className="text-muted normal-case tracking-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={200}
              rows={2}
              placeholder="Welcome to the team…"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text placeholder:text-muted focus:border-accent focus:outline-none resize-none"
            />
          </div>

          {error && (
            <p className="text-xs font-mono text-red">{error}</p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={!email.trim() || invite.isPending}
              className="flex-1"
            >
              {invite.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
